// The new organisation set-up wizard (and Settings → Organisation → "What you make"): the industries an organisation
// can choose, what each switches on, and the starter lists offered. Generic for every country: nothing here is tied to
// one place. Switching an industry off only hides its menus and fields; no data is ever deleted.

export const INDUSTRIES = [
  ["workwear", "Workwear and PPE"],
  ["fashion", "Fashion"],
  ["footwear", "Footwear"],
  ["accessories", "Accessories"],
] as const;
export type Industry = (typeof INDUSTRIES)[number][0];
export const INDUSTRY_KEYS: Industry[] = INDUSTRIES.map(([k]) => k);
export const industryLabel = (k: string) => INDUSTRIES.find(([x]) => x === k)?.[1] || k;
export const normalizeIndustries = (v: unknown): Industry[] =>
  INDUSTRY_KEYS.filter((k) => Array.isArray(v) && v.includes(k));

/** The feature switches an industry choice turns on. The first three are the existing switches of
 *  Settings → Features (they are set when the industries are saved); the others follow the industries directly. */
export interface IndustryFeatures {
  standards: boolean; catAtpvFr: boolean; fabricTest: boolean; season: boolean; garment: boolean; footwear: boolean; washCare: boolean;
}
export function industryFeatures(list: readonly string[]): IndustryFeatures {
  // Nothing chosen yet (a new organisation before step 2): nothing is hidden.
  if (!list.length) return { standards: true, catAtpvFr: true, fabricTest: true, season: true, garment: true, footwear: true, washCare: true };
  const has = (k: Industry) => list.includes(k);
  const garment = has("workwear") || has("fashion") || has("accessories");
  return {
    standards: has("workwear"), catAtpvFr: has("workwear"), fabricTest: has("workwear"),
    season: has("fashion"),
    garment, footwear: has("footwear"),
    washCare: garment,
  };
}
/** The switches that follow the industries directly (added to the organisation's features in the session). */
export const INDUSTRY_ONLY_KEYS = ["fabricTest", "garment", "footwear", "washCare"] as const;
/** The existing Settings → Features switches set when the industries are saved. */
export const INDUSTRY_SET_KEYS = ["standards", "catAtpvFr", "season"] as const;

/** "What you get": each line On or Off for the industries chosen. */
export const WHAT_YOU_GET: Array<{ label: string; on: (f: IndustryFeatures) => boolean; always?: boolean }> = [
  { label: "Standards and certificates", on: (f) => f.standards },
  { label: "FR / PPE labels", on: (f) => f.catAtpvFr },
  { label: "Bulk fabric testing", on: (f) => f.fabricTest },
  { label: "Seasons and collections", on: (f) => f.season },
  { label: "Garment sizes and garment QC checklist", on: (f) => f.garment },
  { label: "Footwear sizes, footwear naming and footwear QC checklist", on: (f) => f.footwear },
  { label: "Wash care labels", on: (f) => f.washCare },
  { label: "Products, Purchase orders, Time & Action Plan, Sampling, QC, Logistics, Finance, Partners", on: () => true, always: true },
];
/** The industries chosen, for the first step that needs them (strictly: an empty list means "nothing chosen"). */
export function whatYouGet(list: readonly string[]): Array<{ label: string; on: boolean; always: boolean }> {
  const f = list.length ? industryFeatures(list) : { standards: false, catAtpvFr: false, fabricTest: false, season: false, garment: false, footwear: false, washCare: false };
  return WHAT_YOU_GET.map((w) => ({ label: w.label, on: w.always ? true : w.on(f), always: !!w.always }));
}

// ---- Starter lists ------------------------------------------------------------------------------------------------
export const GARMENT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];
export const SHOE_SIZES = Array.from({ length: 13 }, (_, i) => `EU ${36 + i}`);
export const STARTER_STANDARDS = ["EN ISO 11612", "EN ISO 11611", "IEC 61482-2", "EN ISO 20471", "EN 1149-5", "NFPA 2112"];
export const starterSeasons = (year: number) => [`Spring/Summer ${year}`, `Autumn/Winter ${year}`, `Spring/Summer ${year + 1}`, `Autumn/Winter ${year + 1}`];
export const STARTER_COLOURS: Array<{ value: string; code: string; hex: string }> = [
  { value: "Black", code: "BLK", hex: "#111111" }, { value: "White", code: "WHT", hex: "#FFFFFF" }, { value: "Navy", code: "NVY", hex: "#1F2A44" },
  { value: "Grey", code: "GRY", hex: "#8A8D91" }, { value: "Royal Blue", code: "RBL", hex: "#2B4FA3" }, { value: "Red", code: "RED", hex: "#C62828" },
  { value: "Hi-vis Orange", code: "HVO", hex: "#FF6A13" }, { value: "Hi-vis Yellow", code: "HVY", hex: "#E4F21C" }, { value: "Khaki", code: "KHA", hex: "#B8A574" },
  { value: "Green", code: "GRN", hex: "#2E7D32" },
];
export type StarterListKey = "garmentSizes" | "shoeSizes" | "standards" | "seasons" | "colours" | "qc";
/** Which starter lists match the industries chosen in step 2 (Colours and the QC checklist always). */
export function starterListsFor(list: readonly string[]): StarterListKey[] {
  const f = industryFeatures(list);
  return [
    ...(f.garment ? ["garmentSizes" as const] : []),
    ...(f.footwear ? ["shoeSizes" as const] : []),
    ...(list.includes("workwear") ? ["standards" as const] : []),
    ...(list.includes("fashion") ? ["seasons" as const] : []),
    "colours", "qc",
  ];
}
/** Add only what isn't there yet (by name, ignoring case and spaces at the ends); returns the merged list and what was added. */
export function addMissing<T extends { value: string }>(existing: T[], wanted: T[]): { list: T[]; added: string[] } {
  const seen = new Set(existing.map((e) => String(e.value).trim().toLowerCase()));
  const list = [...existing];
  const added: string[] = [];
  for (const w of wanted) {
    const k = w.value.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    list.push(w);
    added.push(w.value);
  }
  return { list, added };
}

// ---- Team (step 4) ----------------------------------------------------------------------------------------------------
export const SETUP_ROLES = [
  ["merchandiser", "Merchandiser"], ["qcinspector", "QC inspector"], ["finance", "Finance"], ["viewer", "Viewer"], ["admin", "Admin"],
] as const;
export type SetupRole = (typeof SETUP_ROLES)[number][0];
/** A wizard role: the user's privilege and the existing per-user permission template (starting point) it applies. */
export const SETUP_ROLE_MAP: Record<SetupRole, { role: "Admin" | "Merchandiser" | "QC inspector"; startFrom: string }> = {
  merchandiser: { role: "Merchandiser", startFrom: "merchandiser" },
  qcinspector: { role: "QC inspector", startFrom: "qcinspector" },
  finance: { role: "Merchandiser", startFrom: "finance" },
  viewer: { role: "Merchandiser", startFrom: "viewer" },
  admin: { role: "Admin", startFrom: "admin" },
};
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Each team row's problems: name, email format, duplicates (within the rows and with people already in Falcon). */
export function teamProblems(rows: Array<{ name: string; email: string; role: string }>, existingEmails: string[] = []): Array<{ name?: string; email?: string; role?: string }> {
  const taken = new Set(existingEmails.map((e) => e.trim().toLowerCase()));
  const count = new Map<string, number>();
  for (const r of rows) { const e = r.email.trim().toLowerCase(); if (e) count.set(e, (count.get(e) || 0) + 1); }
  return rows.map((r) => {
    const e = r.email.trim().toLowerCase();
    const p: { name?: string; email?: string; role?: string } = {};
    if (!r.name.trim()) p.name = "Enter a name";
    if (!e) p.email = "Enter an email address";
    else if (!EMAIL_RE.test(e)) p.email = "Check the email address";
    else if ((count.get(e) || 0) > 1) p.email = "This email is entered twice";
    else if (taken.has(e)) p.email = "This person is already in Falcon";
    if (!SETUP_ROLES.some(([k]) => k === r.role)) p.role = "Choose a role";
    return p;
  });
}

export const SETUP_STEPS = ["Your company", "What you make", "Starter lists", "Your team", "Done"] as const;
